import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import config from 'config';
import { noop } from 'lodash';
import React from 'react';

import { isFeatureEnabled } from './feature';
import { blurSensitiveData } from './filter';

const EmptyErrorBoundary: React.FC<{ children?: React.ReactNode }> = (props) => (
	<>{props.children}</>
);

let bugsnagClient: any = {
	getPlugin: () => ({ createErrorBoundary: () => EmptyErrorBoundary }),
	leaveBreadcrumb: noop,
	notify: noop,
	user: { id: null },
};

// Intentional fetch cancellations reject with an AbortError ("signal is aborted without
// reason"). When we abort an in-flight request (e.g. SearchPage cancelling a search on
// filter/tab change), Chromium surfaces the response body stream's internal cancellation
// as an unhandled rejection that the per-request `.catch()` can't intercept, so
// autoDetectErrors reports it. These are expected cancellations, not bugs — discard them.
const isAbortError = (error: unknown): boolean =>
	!!error && typeof error === 'object' && (error as { name?: string }).name === 'AbortError';

if (isFeatureEnabled('bugsnag')) {
	bugsnagClient = Bugsnag.start({
		apiKey: BUGSNAG_API_KEY,
		autoDetectErrors: config.bugsnag.notify,
		enabledReleaseStages: ['production', 'staging'],
		releaseStage: config.bugsnag.stage,
		plugins: [new BugsnagPluginReact()],
		appVersion: APP_VERSION,
		onError: (event) => {
			if (
				isAbortError(event.originalError) ||
				event.errors.some((e) => e.errorClass === 'AbortError')
			) {
				return false;
			}
			return true;
		},
	});
}

export function notifyBugsnagNetwork(service: string, method: string, args: any = null) {
	if (!isFeatureEnabled('bugsnag')) return;

	const blurredArgs = blurSensitiveData(args);
	bugsnagClient.leaveBreadcrumb('Network Request', {
		args: JSON.stringify(blurredArgs, null, 2),
		method,
		service,
	});
}

export function notifyBugsnagHandledError(err: Error) {
	if (!isFeatureEnabled('bugsnag')) return;

	bugsnagClient.notify(err);
}

export default bugsnagClient;
